import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Briefcase, MapPin, Clock, ChevronRight,
  ToggleLeft, ToggleRight, Loader2, CheckCircle2,
} from 'lucide-react'
import { supabase } from '@/config/supabase'
import useAuthStore from '@/stores/authStore'
import { buildRoute } from '@/config/routes'
import WorkerLayout from '@/components/worker/WorkerLayout'

// ── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_UI = {
  assigned:         { label: 'Assigned',         color: 'text-blue-400 bg-blue-500/10 border-blue-500/25'          },
  en_route:         { label: 'En Route',          color: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/25'    },
  in_progress:      { label: 'In Progress',       color: 'text-siglat-teal bg-siglat-teal/10 border-siglat-teal/25'},
  pending_approval: { label: 'Pending Approval',  color: 'text-orange-400 bg-orange-500/10 border-orange-500/25'   },
}

function cityOf(address) {
  if (!address) return ''
  const parts = address.split(',')
  return parts.length > 1 ? parts[parts.length - 2]?.trim() : parts[0]?.trim()
}

function fmtDate(dateStr, time) {
  if (!dateStr) return ''
  const d = new Date(dateStr + 'T00:00:00')
  const label = d.toLocaleDateString('en-PH', { month: 'short', day: 'numeric' })
  return time ? `${label} · ${time}` : label
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function WorkerDashboard() {
  const navigate = useNavigate()
  const profile  = useAuthStore((s) => s.profile)

  const [jobs,       setJobs]       = useState([])
  const [loading,    setLoading]    = useState(true)
  const [available,  setAvailable]  = useState(false)
  const [toggling,   setToggling]   = useState(false)
  const [doneCount,  setDoneCount]  = useState(0)

  useEffect(() => {
    if (!profile?.id) return
    fetchWorkerProfile()
    fetchJobs()
    fetchDoneCount()
  }, [profile?.id]) // eslint-disable-line

  async function fetchWorkerProfile() {
    const { data } = await supabase
      .from('worker_profiles')
      .select('is_available')
      .eq('id', profile.id)
      .maybeSingle()
    if (data) setAvailable(data.is_available ?? false)
  }

  async function fetchJobs() {
    setLoading(true)
    const { data } = await supabase
      .from('bookings')
      .select(`
        id, ref_number, status,
        scheduled_date, scheduled_time, address,
        service_categories ( name )
      `)
      .eq('worker_id', profile.id)
      .in('status', ['assigned', 'en_route', 'in_progress', 'pending_approval'])
      .order('scheduled_date', { ascending: true })
    setJobs(data ?? [])
    setLoading(false)
  }

  async function fetchDoneCount() {
    const { count } = await supabase
      .from('bookings')
      .select('id', { count: 'exact', head: true })
      .eq('worker_id', profile.id)
      .eq('status', 'completed')
    setDoneCount(count ?? 0)
  }

  async function toggleAvailability() {
    const next = !available
    setAvailable(next)
    setToggling(true)
    const { error } = await supabase
      .from('worker_profiles')
      .update({ is_available: next })
      .eq('id', profile.id)
    if (error) {
      console.error('Availability toggle failed:', error)
      setAvailable(!next)
    }
    setToggling(false)
  }

  return (
    <WorkerLayout available={available}>
      <div className="px-4 py-5 max-w-lg mx-auto flex flex-col gap-5">

        {/* ── Stats row ─────────────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-surface-raised border border-surface-border rounded-xl p-4">
            <p className="text-2xl font-bold text-ink-primary">{jobs.length}</p>
            <p className="text-xs text-ink-muted mt-0.5">Active jobs</p>
          </div>
          <div className="bg-surface-raised border border-surface-border rounded-xl p-4">
            <p className="text-2xl font-bold text-ink-primary">{doneCount}</p>
            <p className="text-xs text-ink-muted mt-0.5">Completed</p>
          </div>
        </div>

        {/* ── Availability toggle ───────────────────────────────────────────── */}
        <button
          onClick={toggleAvailability}
          disabled={toggling}
          className={`w-full flex items-center justify-between gap-3 p-4 rounded-xl border transition-colors
            ${available
              ? 'bg-siglat-teal/8 border-siglat-teal/30 hover:bg-siglat-teal/12'
              : 'bg-surface-raised border-surface-border hover:bg-surface-overlay'}`}
        >
          <div className="text-left">
            <p className={`text-sm font-semibold ${available ? 'text-siglat-teal' : 'text-ink-primary'}`}>
              {available ? 'You are available' : 'You are offline'}
            </p>
            <p className="text-xs text-ink-muted mt-0.5">
              {available ? 'Admin can assign you to new jobs' : 'Toggle on to receive assignments'}
            </p>
          </div>
          {toggling
            ? <Loader2 className="w-9 h-9 text-siglat-teal animate-spin shrink-0" />
            : available
              ? <ToggleRight className="w-10 h-10 text-siglat-teal shrink-0" />
              : <ToggleLeft  className="w-10 h-10 text-ink-muted shrink-0" />
          }
        </button>

        {/* ── Active jobs list ──────────────────────────────────────────────── */}
        <div>
          <p className="text-xs text-ink-muted uppercase tracking-widest font-semibold mb-3 px-1">
            Active Jobs
          </p>

          {loading ? (
            <div className="flex flex-col gap-2">
              {[1, 2].map((n) => (
                <div key={n} className="h-[72px] bg-surface-raised border border-surface-border rounded-xl animate-pulse" />
              ))}
            </div>
          ) : jobs.length === 0 ? (
            <div className="bg-surface-raised border border-surface-border rounded-xl p-8 text-center">
              <Briefcase className="w-8 h-8 text-ink-muted mx-auto mb-3 opacity-30" />
              <p className="text-sm font-medium text-ink-secondary">No active jobs</p>
              <p className="text-xs text-ink-muted mt-1">
                {available ? 'Waiting for assignment from admin' : 'Toggle available to receive new jobs'}
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {jobs.map((job) => {
                const ui = STATUS_UI[job.status] ?? { label: job.status, color: 'text-ink-muted bg-surface-overlay border-surface-border' }
                return (
                  <button
                    key={job.id}
                    onClick={() => navigate(buildRoute.workerJob(job.id))}
                    className="bg-surface-raised border border-surface-border rounded-xl p-4 text-left hover:bg-surface-overlay active:scale-[0.99] transition-all w-full"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <p className="text-sm font-semibold text-ink-primary">
                            {job.service_categories?.name ?? 'Service'}
                          </p>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${ui.color}`}>
                            {ui.label}
                          </span>
                        </div>
                        <p className="text-[10px] text-ink-muted font-mono mb-1.5">{job.ref_number}</p>
                        <div className="flex items-center gap-3 flex-wrap">
                          {job.scheduled_date && (
                            <span className="flex items-center gap-1 text-xs text-ink-muted">
                              <Clock className="w-3 h-3 shrink-0" />
                              {fmtDate(job.scheduled_date, job.scheduled_time)}
                            </span>
                          )}
                          {job.address && (
                            <span className="flex items-center gap-1 text-xs text-ink-muted">
                              <MapPin className="w-3 h-3 shrink-0" />
                              {cityOf(job.address)}
                            </span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-ink-muted shrink-0 mt-0.5" />
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {/* ── Completed indicator ───────────────────────────────────────────── */}
        {doneCount > 0 && (
          <div className="flex items-center gap-2 px-1">
            <CheckCircle2 className="w-4 h-4 text-green-400 shrink-0" />
            <p className="text-xs text-ink-muted">
              {doneCount} job{doneCount !== 1 ? 's' : ''} completed — view in{' '}
              <button
                onClick={() => navigate('/worker/earnings')}
                className="text-siglat-teal underline underline-offset-2"
              >
                Earnings
              </button>
            </p>
          </div>
        )}

      </div>
    </WorkerLayout>
  )
}
